import { useState } from 'react';
import { api, type IssuedAgentToken, type Me } from '../../api';
import { formatDateTime, relativeTime } from '../../format';
import { useAction, useLoader } from '../../hooks';
import { Alert } from '../Alert';
import { ConfirmDialog } from '../ConfirmDialog';
import { Tag } from '../Tag';

/**
 * Jeton d'agent. Le jeton en clair n'est renvoyé qu'UNE fois par le serveur : il ne vit que dans l'état de ce composant
 * (`issued`), n'est ni stocké (localStorage, URL, titre), ni journalisé, ni relu ; il est effacé à la fermeture de l'encadré.
 */
export function TokenPanel({
  instanceId,
  kind,
  me,
  onChanged,
}: {
  instanceId: string;
  kind: 'cloud' | 'local';
  me: Me;
  onChanged?: () => void;
}) {
  const { data: tokens, error, reload } = useLoader(() => api.agentTokens(instanceId), instanceId);
  const action = useAction();
  const [issued, setIssued] = useState<IssuedAgentToken | null>(null);
  const [copy, setCopy] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [dialog, setDialog] = useState<'issue' | 'revoke' | null>(null);
  const [push, setPush] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canPush = kind === 'cloud' && me.provisioningMode !== 'disabled';
  const active = (tokens ?? []).filter((t) => !t.revokedAt);

  async function issue() {
    setNotice(null);
    const result = await action.run(() => api.issueAgentToken(instanceId, canPush && push));
    setDialog(null);
    if (result) {
      setIssued(result);
      setCopy('idle');
      await reload();
      onChanged?.();
    }
  }

  async function revoke() {
    setNotice(null);
    const result = await action.run(() => api.revokeAgentTokens(instanceId));
    setDialog(null);
    if (result) {
      setIssued(null);
      setNotice(
        result.revoked > 0 ? `${result.revoked} jeton(s) révoqué(s).` : 'Aucun jeton actif à révoquer.',
      );
      await reload();
    }
  }

  async function copyToken() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopy('ok');
    } catch {
      setCopy('fail');
    }
  }

  function closeToken() {
    setIssued(null);
    setCopy('idle');
  }

  return (
    <div className="stack">
      {action.error && <Alert>{action.error}</Alert>}
      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}

      {issued && (
        <div className="token-box" role="region" aria-label="Nouveau jeton d'agent">
          <Alert kind="warning">
            <strong>Copiez ce jeton maintenant.</strong> Il ne sera plus jamais affiché : WHCC n'en conserve
            que l'empreinte. Ne le collez ni dans un ticket ni dans un chat.
          </Alert>
          <div className="token-row">
            <label htmlFor="agent-token-value" className="sr-only">
              Jeton d'agent
            </label>
            <input
              id="agent-token-value"
              type="text"
              readOnly
              value={issued.token}
              autoComplete="off"
              spellCheck={false}
              data-lpignore="true"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="btn btn-primary" onClick={() => void copyToken()}>
              Copier le jeton
            </button>
          </div>
          <div aria-live="polite">
            {copy === 'ok' && <p className="muted">Jeton copié dans le presse-papiers.</p>}
            {copy === 'fail' && (
              <p className="field-error">
                Copie impossible : sélectionnez le jeton ci-dessus et copiez-le à la main.
              </p>
            )}
          </div>
          <p className="muted">
            {issued.pushedToCluster
              ? "Le jeton a aussi été posé dans le Secret de l'instance (clé whcc-agent-token) : elle le lira à son prochain démarrage."
              : "Le jeton n'a pas été posé dans le cluster : à fournir à l'agent (variable WHCC_AGENT_TOKEN ou identifiants de l'instance)."}{' '}
            Les jetons précédents sont révoqués.
          </p>
          <button type="button" className="btn" onClick={closeToken}>
            J'ai copié le jeton, fermer et l'effacer
          </button>
        </div>
      )}

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={action.busy}
          onClick={() => {
            setPush(canPush);
            setDialog('issue');
          }}
        >
          {active.length > 0 ? 'Remplacer le jeton' : 'Émettre un jeton'}
        </button>
        <button
          type="button"
          className="btn btn-danger-outline"
          disabled={action.busy || active.length === 0}
          onClick={() => setDialog('revoke')}
        >
          Révoquer le jeton
        </button>
        {tokens && (
          <span className="muted">
            {active.length > 0 ? 'Un jeton actif.' : 'Aucun jeton actif : l’agent ne peut pas se connecter.'}
          </span>
        )}
      </div>

      {tokens === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des jetons…
        </p>
      )}
      {tokens && tokens.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Jetons d'agent de l'instance (aucune valeur en clair)</caption>
            <thead>
              <tr>
                <th scope="col">Préfixe</th>
                <th scope="col">État</th>
                <th scope="col">Créé</th>
                <th scope="col">Dernière utilisation</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <th scope="row">
                    <code>{t.prefix}…</code>
                  </th>
                  <td>
                    {t.revokedAt ? (
                      <Tag tone="gray" title={formatDateTime(t.revokedAt)}>
                        Révoqué
                      </Tag>
                    ) : (
                      <Tag tone="green">Actif</Tag>
                    )}
                  </td>
                  <td className="nowrap">
                    {formatDateTime(t.createdAt)}
                    {t.createdBy ? <span className="muted"> · {t.createdBy}</span> : null}
                  </td>
                  <td>{t.lastUsedAt ? relativeTime(t.lastUsedAt) : 'jamais'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog === 'issue' && (
        <ConfirmDialog
          title={active.length > 0 ? 'Remplacer le jeton d’agent' : 'Émettre un jeton d’agent'}
          confirmLabel={active.length > 0 ? 'Remplacer le jeton' : 'Émettre le jeton'}
          busy={action.busy}
          onConfirm={() => void issue()}
          onCancel={() => setDialog(null)}
        >
          {active.length > 0 && (
            <p>
              Le jeton actuel sera <strong>révoqué</strong> : l'agent de l'instance ne pourra plus se
              connecter tant qu'il n'aura pas reçu le nouveau.
            </p>
          )}
          <p>Le jeton sera affiché une seule fois.</p>
          <label className="check">
            <input
              type="checkbox"
              checked={canPush && push}
              disabled={!canPush}
              onChange={(e) => setPush(e.target.checked)}
            />
            Pousser dans le cluster (Secret de l'instance)
          </label>
          {!canPush && (
            <p className="muted">
              {kind === 'local'
                ? "Indisponible pour une instance locale : le jeton est à fournir à l'agent."
                : 'Indisponible : le provisionnement est désactivé sur ce serveur.'}
            </p>
          )}
        </ConfirmDialog>
      )}
      {dialog === 'revoke' && (
        <ConfirmDialog
          title="Révoquer le jeton d’agent"
          confirmLabel="Révoquer le jeton"
          danger
          busy={action.busy}
          onConfirm={() => void revoke()}
          onCancel={() => setDialog(null)}
        >
          <p>
            L'agent de l'instance sera refusé (<code>401</code>) à son prochain appel. L'instance continue de
            fonctionner avec sa dernière configuration valide.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
